import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API, apiErr } from "@/lib/api";
import { hasPerm } from "@/lib/helpers";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import CollectFeeDialog from "@/pages/CollectFeeDialog";
import {
  ChevronDown, Eye, Pencil, Trash2, ReceiptIndianRupee, CalendarCheck,
  Home, FileText, Award, KeyRound, Wallet, IdCard, HandCoins, Sparkles,
} from "lucide-react";

export default function StudentRowActions({ student, user, onChanged, onEdit, onDelete }) {
  const nav = useNavigate();
  const token = localStorage.getItem("token");
  const [del, setDel] = useState(false);
  const [houseOpen, setHouseOpen] = useState(false);
  const [house, setHouse] = useState(student.house || "");
  const [collectOpen, setCollectOpen] = useState(false);
  const [login, setLogin] = useState(null);
  const [sending, setSending] = useState(false);

  const doDelete = async () => {
    try { await api.delete(`/students/${student.id}`); toast.success("Student deleted"); onChanged(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const saveHouse = async () => {
    try { await api.patch(`/students/${student.id}/house`, { house }); toast.success("House updated"); setHouseOpen(false); onChanged(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const sendLogin = async () => {
    try { const r = await api.post(`/students/${student.id}/send-login`); setLogin(r.data); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const loginMessage = () => login ? `SSSPS Portal Login\nStudent: ${student.name}\nUsername: ${login.username}\nPassword: ${login.password}\nLogin at your school portal.` : "";
  const sendVia = async (channel) => {
    setSending(true);
    try {
      const r = await api.post("/notify/message", { student_id: student.id, channel, message: loginMessage() });
      if (r.data.sent) toast.success(`Sent via ${channel === "whatsapp" ? "WhatsApp" : "SMS"} to ${r.data.to}`);
      else toast.info(r.data.note || "Could not send");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSending(false); }
  };
  const openPdf = (kind) => window.open(`${API}/students/${student.id}/${kind}?token=${token}`, "_blank");

  const assignFees = async () => {
    try {
      const { data } = await api.post("/students/assign-fees", { student_ids: [student.id] });
      if (data.assigned) { toast.success("Fees assigned from the class fee structure"); onChanged(); }
      else if (data.already) toast.info("This student already has fees assigned");
      else if ((data.no_structure || []).length) {
        toast.info(`No fee structure for ${data.no_structure.join(", ")} — opening Fee Structure page…`);
        setTimeout(() => nav("/fees"), 1200);
      } else toast.info("No fees assigned");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Assign failed"); }
  };

  const Item = ({ icon: Icon, label, onClick, testid, danger }) => (
    <DropdownMenuItem onClick={onClick} data-testid={testid} className={danger ? "text-rose-600 focus:text-rose-600" : ""}>
      <Icon size={15} className="mr-2" />{label}
    </DropdownMenuItem>
  );

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 gap-1 text-xs" data-testid={`actions-${student.admission_number}`} onClick={(e) => e.stopPropagation()}>
            Action
            <ChevronDown size={14} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52" onClick={(e) => e.stopPropagation()}>
          <Item icon={Eye} label="View Student" onClick={() => nav(`/students/${student.id}`)} testid={`view-${student.admission_number}`} />
          {hasPerm(user, "edit_student") && <Item icon={Pencil} label="Edit Student" onClick={() => onEdit(student)} testid={`edit-${student.admission_number}`} />}
          {hasPerm(user, "view_fees") && <Item icon={ReceiptIndianRupee} label="View Fees" onClick={() => nav(`/students/${student.id}?tab=fees`)} />}
          {hasPerm(user, "collect_fee") && <Item icon={HandCoins} label="Collect Fees" onClick={() => nav(`/fees/collect/${student.id}`)} testid={`collect-fees-${student.admission_number}`} />}
          {hasPerm(user, "add_discount") && <Item icon={Wallet} label="Update Fees" onClick={() => nav(`/fees/edit/${student.id}`)} testid={`update-fees-${student.admission_number}`} />}
          {hasPerm(user, "add_discount") && <Item icon={Sparkles} label="Assign Fees" onClick={assignFees} testid={`assign-fees-${student.admission_number}`} />}
          {hasPerm(user, "view_attendance") && <Item icon={CalendarCheck} label="View Attendance" onClick={() => nav(`/students/${student.id}?tab=attendance`)} />}
          <DropdownMenuSeparator />
          {hasPerm(user, "edit_student") && <Item icon={Home} label="Add House" onClick={() => setHouseOpen(true)} />}
          {hasPerm(user, "print") && <Item icon={FileText} label="Create TC" onClick={() => openPdf("tc-pdf")} />}
          {hasPerm(user, "print") && <Item icon={Award} label="Create Certificate" onClick={() => openPdf("certificate-pdf")} />}
          {hasPerm(user, "print") && <Item icon={IdCard} label="Create ID Card" onClick={() => openPdf("idcard-pdf")} testid={`idcard-${student.admission_number}`} />}
          {hasPerm(user, "manage_users") && <Item icon={KeyRound} label="Send Login Details" onClick={sendLogin} testid={`send-login-${student.admission_number}`} />}
          {hasPerm(user, "delete") && <><DropdownMenuSeparator /><Item icon={Trash2} label="Delete Student" danger onClick={() => onDelete ? onDelete(student) : setDel(true)} testid={`delete-${student.admission_number}`} /></>}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={del} onOpenChange={setDel}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this student?</AlertDialogTitle>
            <AlertDialogDescription>{student.name} ({student.admission_number}) will be deactivated. This can be reversed by an admin.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={doDelete} data-testid="confirm-delete-student">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={houseOpen} onOpenChange={setHouseOpen}>
        <DialogContent><DialogHeader><DialogTitle>Assign House — {student.name}</DialogTitle></DialogHeader>
          <div><Label>House</Label><Input value={house} onChange={(e) => setHouse(e.target.value)} placeholder="e.g. Blue / Red / Green" data-testid="house-input" /></div>
          <DialogFooter><Button onClick={saveHouse} data-testid="save-house-btn">Save House</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <CollectFeeDialog open={collectOpen} onOpenChange={setCollectOpen} studentId={student.id} onCollected={onChanged} />

      <Dialog open={!!login} onOpenChange={(o) => !o && setLogin(null)}>
        <DialogContent><DialogHeader><DialogTitle>Login Details Generated</DialogTitle></DialogHeader>
          {login && (
            <div className="space-y-2 text-sm">
              <div className="bg-slate-50 rounded-lg p-4 font-mono">
                <div>Username: <span className="font-bold" data-testid="login-username">{login.username}</span></div>
                <div>Password: <span className="font-bold" data-testid="login-password">{login.password}</span></div>
              </div>
              <p className="text-amber-600 text-xs">{login.note}</p>
            </div>
          )}
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={() => { navigator.clipboard?.writeText(loginMessage()); toast.success("Copied"); }} data-testid="copy-login-btn">Copy</Button>
            <Button variant="outline" disabled={sending} onClick={() => sendVia("sms")} data-testid="send-sms-btn">Send SMS</Button>
            <Button disabled={sending} onClick={() => sendVia("whatsapp")} data-testid="send-whatsapp-btn">Send WhatsApp</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}